import axios from 'axios';
import toast from 'react-hot-toast';
import { resolveApiBaseURL } from './apiBase';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  allowAbsoluteUrls: false,
  // Without a timeout, a hung proxy/API leaves useQuery stuck on isLoading forever ("Loading..." only).
  timeout: 60_000,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    config.baseURL = resolveApiBaseURL();
    const token = localStorage.getItem('auth-storage');
    if (token) {
      try {
        const parsed = JSON.parse(token);
        if (parsed.state?.token) {
          config.headers.Authorization = `Bearer ${parsed.state.token}`;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    // Let axios set Content-Type (with boundary) for FormData; default application/json
    // or a bare "multipart/form-data" (no boundary) breaks multer → "No file received".
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const h = config.headers;
      if (h) {
        if (typeof h.delete === 'function') {
          h.delete('Content-Type');
          h.delete('content-type');
        } else {
          delete h['Content-Type'];
          delete h['content-type'];
        }
      }
      // Large PDF/photo uploads — do not apply the default 60s cap unless caller sets timeout
      if (config.timeout === undefined || config.timeout === 60_000) {
        config.timeout = 0;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // React Query / Strict Mode / navigation cancels in-flight requests ?? never toast those.
    if (axios.isCancel?.(error)) {
      return Promise.reject(error);
    }
    const ec = error?.code;
    const en = error?.name;
    const em = String(error?.message || '').toLowerCase();
    if (
      ec === 'ERR_CANCELED' ||
      en === 'CanceledError' ||
      en === 'AbortError' ||
      em.includes('cancel') ||
      em.includes('aborted')
    ) {
      return Promise.reject(error);
    }

    if (error.response) {
      const { status, data } = error.response;
      const isLoginRequest = error.config?.url?.includes('/auth/login');

      if (status === 401 && !isLoginRequest) {
        // Unauthorized (except login failure) - redirect to login
        localStorage.removeItem('auth-storage');
        window.location.href = '/login';
      } else if (status === 403) {
        if (!error.config?.skipErrorToast) {
          toast.error('Access denied');
        }
      } else if (status === 503) {
        const u = String(error.config?.url || '');
        if (u.includes('/site-inspection-points/')) {
          /* Optional migrations (033+); checklist UIs treat as empty ?? avoid toast spam */
        } else {
          const detail =
            (data && typeof data === 'object' && (data.error || data.message)) || null;
          toast.error(
            detail ||
              'Database or server unavailable. Check that MySQL and the backend are running.'
          );
        }
      } else if (status >= 500) {
        if (!error.config?.skipErrorToast) {
          const detail =
            (data && typeof data === 'object' && (data.error || data.message)) ||
            null;
          toast.error(
            detail
              ? `Server error: ${detail}`
              : 'Server error. Please try again later.'
          );
        }
      } else if (status === 429) {
        // Many parallel refetches can all 429 — toast once per window
        const now = Date.now();
        if (!api._last429ToastAt || now - api._last429ToastAt > 20000) {
          api._last429ToastAt = now;
          toast.error(
            (data && data.error) ||
              'Too many requests — wait about a minute, then try again.'
          );
        }
      } else if (
        status === 404 &&
        (error.config?.url?.includes('compliance-amendments') ||
          error.config?.url?.includes('applications/compliance') ||
          error.config?.url?.includes('site-inspection-points'))
      ) {
        // Handled by component / checklist UI ?? no toast spam
      } else if (
        status === 409 &&
        String(error.config?.method || '').toLowerCase() === 'post' &&
        /^\/buildings\/?(\?|$)/.test(String(error.config?.url || '').split('?')[0])
      ) {
        // Duplicate address on register ?? Amend UI handles and links existing row
      } else {
        if (!error.config?.skipErrorToast) {
          toast.error(data?.error || 'An error occurred');
        }
      }
    } else if (error.request) {
      if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
        return Promise.reject(error);
      }
      const msgLower = String(error.message || '').toLowerCase();
      if (msgLower.includes('timeout')) {
        toast.error('Request timed out. Check that the API is running (web/backend, port 3000).');
      } else if (error.code !== 'ECONNABORTED') {
        toast.error('Network error. Please check your connection.');
      }
      // ECONNABORTED without "timeout" (e.g. React Query cancel) ?? no toast
    } else {
      // No response and no request: often a cancelled or malformed error; avoid alarming the user.
      if (import.meta.env.DEV) {
        console.warn('[api] error without response/request', error?.message || error);
      }
    }

    return Promise.reject(error);
  }
);

export { resolveApiBaseURL, getSiteInspectionPingUrl, getApiPingUrl } from './apiBase';
export default api;
